// Brevo Email Service
// Handles sending emails via Brevo API

import { emailConfig } from '../config/emailConfig';
import { processEmailTemplate } from './emailTemplateService';
import { LOGO_URL } from './logoConstants';

export interface EmailResult {
    success: boolean;
    messageId?: string;
    error?: string;
}

export interface AppointmentFormData {
    name: string;
    email: string;
    phone?: string;
    Phone?: string;
    service?: string;
    date?: string;
    message?: string;
    language?: string;
    ip_address?: string;
    user_agent?: string;
}

// Service name mapping
const serviceMapping: { [key: string]: { en: string; pt: string } } = {
    'general_mechanics': { en: 'General Mechanics', pt: 'Mecânica Geral' },
    'air_conditioning': { en: 'Air Conditioning', pt: 'Ar Condicionado' },
    'alignment': { en: 'Alignment', pt: 'Alinhamento' },
    'batteries': { en: 'Batteries', pt: 'Baterias' },
    'brakes': { en: 'Brakes', pt: 'Travões' },
    'diagnostic_test': { en: 'Diagnostic Test', pt: 'Teste de Diagnóstico' },
    'workshop_package': { en: 'Workshop Services Pack', pt: 'Pacote de Serviços de Oficina' },
    'washing': { en: 'Washing', pt: 'Lavagem' },
    'headlight_restoration': { en: 'Headlight Restoration', pt: 'Restauro de Faróis' },
    'inspection': { en: 'Inspection', pt: 'Inspeção' },
    'roady_review': { en: 'Roady Review', pt: 'Revisão Roady' },
    'shock_absorbers': { en: 'Shock Absorbers', pt: 'Amortecedores' }
};

/**
 * Get service name in the specified language
 */
function getServiceName(service: string | undefined, language: string = 'pt'): string {
    if (!service) return '';
    
    const mapping = serviceMapping[service];
    if (mapping) {
        return mapping[language as 'en' | 'pt'] || mapping.pt;
    }
    return service;
}

/**
 * Get reCAPTCHA token from the page
 */
export function getRecaptchaToken(): string {
    try {
        if (typeof (window as any).grecaptcha !== 'undefined') {
            const token = (window as any).grecaptcha.getResponse();
            if (token) {
                return token;
            }
        }
        
        // Fallback: check hidden textarea
        const textarea = document.querySelector('#g-recaptcha-response') as HTMLTextAreaElement;
        if (textarea && textarea.value) {
            return textarea.value;
        }
    } catch (error) {
    }
    
    return '';
}

// Single email interface for batch sending
export interface SingleEmailPayload {
    to: { email: string; name?: string };
    subject: string;
    htmlContent: string;
    sender?: { name: string; email: string };
    attachments?: Array<{ name: string; content: string }>;
}

// Batch email result
export interface BatchEmailResult {
    success: boolean;
    results?: Array<{ success: boolean; messageId?: string; error?: string }>;
    summary?: { total: number; sent: number; failed: number };
    error?: string;
}

/**
 * Send batch emails via Firebase Cloud Function
 * This sends multiple emails in a single request, verifying reCAPTCHA only once
 */
export async function sendBatchEmailsViaBrevo(
    emails: SingleEmailPayload[],
    recaptchaToken?: string
): Promise<BatchEmailResult> {
    try {
        if (!emailConfig.CLOUD_FUNCTION_URL) {
            throw new Error('Cloud Function URL not configured. Please check Firebase Project ID.');
        }

        // Get reCAPTCHA token if not provided
        const token = recaptchaToken || getRecaptchaToken();
        if (!token) {
            throw new Error('reCAPTCHA token not found. Please complete the reCAPTCHA.');
        }

        // Prepare emails with default sender
        const emailsWithSender = emails.map(email => ({
            ...email,
            sender: email.sender || {
                name: emailConfig.BREVO_FROM_NAME || 'Roady',
                email: emailConfig.BREVO_FROM_EMAIL || 'noreply@roady.pt'
            }
        }));

        const payload = {
            emails: emailsWithSender,
            recaptchaToken: token
        };


        const fetchOptions: RequestInit = {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        };

        let response: Response;
        try {
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, { ...fetchOptions, keepalive: true });
        } catch (err: any) {
            console.warn('keepalive fetch failed, retrying without keepalive:', err?.message);
            response = await fetch(emailConfig.CLOUD_FUNCTION_URL, fetchOptions);
        }

        const responseText = await response.text();

        if (!response.ok) {
            throw new Error(`Cloud Function error: ${response.status} - ${responseText}`);
        }

        let result;
        try {
            result = JSON.parse(responseText);
        } catch (e) {
            result = { success: true };
        }


        return {
            success: result.success,
            results: result.results,
            summary: result.summary
        };
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}

// Get company address helper
function getCompanyAddress(): string {
    const fromName = emailConfig.BREVO_FROM_NAME || 'Roady';
    if (fromName.includes('Valongo')) return 'Tv. António Augusto Castro Paupério 36, Valongo';
    if (fromName.includes('Leiria')) return 'Rua da Baquelite 204, Leiria';
    if (fromName.includes('Oliveira')) return 'Av. D. Maria I - Lugar de Aldas, 3720-300 Oliveira de Azeméis';
    if (fromName.includes('São Domingos')) return 'Avenida Antero Brotas – Sítio do Esteval, 2870-122 São Domingos de Rana';
    if (fromName.includes('Montijo')) return 'Av. dos Pescadores 18, 2870-355 Montijo';
    return 'Portugal';
}

/**
 * Prepare appointment user email payload (for batch sending)
 */
export async function prepareAppointmentUserEmail(
    formData: AppointmentFormData,
    language: string = 'pt'
): Promise<SingleEmailPayload | null> {
    try {
        const serviceName = getServiceName(formData.service, language);
        const phone = formData.phone || formData.Phone || '';
        const date = formData.date || '';
        const message = formData.message || '';

        const templateName = language === 'en' 
            ? 'user-appointment-en.html' 
            : 'user-appointment-pt.html';

        let htmlContent = await processEmailTemplate(templateName, {
            name: formData.name || '',
            email: formData.email || '',
            phone: phone,
            service_name: serviceName,
            date: date,
            message: message,
            logo_url: LOGO_URL,
            contact_email: emailConfig.BREVO_TO_EMAIL_CONTACT || '',
            from_name: emailConfig.BREVO_FROM_NAME || 'Roady',
            company_address: getCompanyAddress(),
            year: new Date().getFullYear().toString()
        });
        
        if (!message) {
            htmlContent = htmlContent.replace(/id="message-section"/g, 'id="message-section" style="display: none;"');
        }

        const companyName = emailConfig.BREVO_FROM_NAME || 'Roady';
        const subject = language === 'en' 
            ? `Contact Confirmation - ${companyName}` 
            : `Confirmação de Contacto - ${companyName}`;

        return {
            to: { email: formData.email, name: formData.name || 'Customer' },
            subject,
            htmlContent
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Prepare appointment admin email payload (for batch sending)
 */
export async function prepareAppointmentAdminEmail(
    formData: AppointmentFormData
): Promise<SingleEmailPayload | null> {
    try {
        const serviceName = getServiceName(formData.service, 'pt');
        const phone = formData.phone || formData.Phone || '';
        const date = formData.date || '';
        const message = formData.message || '';
        
        const timestamp = new Date().toLocaleString('pt-PT', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        let htmlContent = await processEmailTemplate('admin-appointment.html', {
            name: formData.name || '',
            email: formData.email || '',
            phone: phone,
            service_name: serviceName,
            date: date,
            message: message,
            timestamp: timestamp
        });
        
        if (!message) {
            htmlContent = htmlContent.replace(/id="message-section"/g, 'id="message-section" style="display: none;"');
        }

        const subject = `Novo Contacto - ${formData.name || 'Cliente'}`;
        const companyName = emailConfig.BREVO_FROM_NAME || 'Roady';

        return {
            to: { email: emailConfig.BREVO_TO_EMAIL_CONTACT, name: `${companyName} Admin` },
            subject,
            htmlContent
        };
    } catch (error: any) {
        return null;
    }
}

/**
 * Send both appointment emails in a single batch (admin + user confirmation)
 */
export async function sendAppointmentEmailsBatch(
    formData: AppointmentFormData,
    language: string = 'pt'
): Promise<BatchEmailResult> {
    try {

        const emails: SingleEmailPayload[] = [];

        // Prepare admin email
        const adminEmail = await prepareAppointmentAdminEmail(formData);
        if (adminEmail) {
            emails.push(adminEmail);
        }

        // Prepare user confirmation email
        const userEmail = await prepareAppointmentUserEmail(formData, language);
        if (userEmail) {
            emails.push(userEmail);
        }

        if (emails.length === 0) {
            throw new Error('No emails to send');
        }

        const result = await sendBatchEmailsViaBrevo(emails);

        if (result.success) {
        } else {
        }

        return result;
    } catch (error: any) {
        return { success: false, error: error.message || 'Unknown error' };
    }
}
